/**
 * ScheduleSubstitutionModal — log an internal product substitution request on a schedule item
 * (Schedule Builder, Round 18).
 *
 * Opens via CustomEvent('open-schedule-substitution-modal', { detail: { item, projectId, conceptId } }):
 *   item — the schedule_items row being changed
 *
 * Shows the current product fields read-only and an editable "proposed" copy (same per-type layout as
 * the item editor) plus a reason. Submitting logs a 'pending' substitution request (source='internal')
 * for review; it does NOT change the live item until approved. Busts ctx.scheduleSubCache.
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { logSubstitution } from '../services/schedules.js'
import { editorFieldsForType } from '../services/scheduleLayout.js'

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }
const roStyle = { ...inputStyle, background: 'var(--aj-bg-muted)', color: 'var(--aj-text-mid)', cursor: 'not-allowed' }

export default function ScheduleSubstitutionModal() {
  const [open, setOpen]       = useState(false)
  const [item, setItem]       = useState(null)
  const [itemLabel, setItemLabel] = useState('')
  const [specSection, setSpecSection] = useState('')
  const [attrs, setAttrs]     = useState({})
  const [reason, setReason]   = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]     = useState(null)

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      const it = d.item || null
      setItem(it)
      setItemLabel(it ? (it.item_label || '') : '')
      setSpecSection(it ? (it.spec_section || '') : '')
      setAttrs(it && it.attrs && typeof it.attrs === 'object' ? { ...it.attrs } : {})
      setReason('')
      setError(null); setSubmitting(false); setOpen(true)
    }
    window.addEventListener('open-schedule-substitution-modal', onOpen)
    return () => window.removeEventListener('open-schedule-substitution-modal', onOpen)
  }, [])

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    function onKey(e) { if (e.key === 'Escape' && !submitting) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', onKey) }
  }, [open, submitting])

  function setAttr(key, val) { setAttrs(prev => ({ ...prev, [key]: val })) }

  async function handleSave() {
    if (!item) return
    if (!reason.trim()) { setError('A reason for the substitution is required.'); return }
    setSubmitting(true); setError(null)
    try {
      await logSubstitution(item.id, { item_label: itemLabel || null, spec_section: specSection || null, attrs: attrs || {} }, reason.trim())
      ctx.scheduleSubCache = undefined
      window.dispatchEvent(new Event('outline-render'))
      setOpen(false)
    } catch (ex) { setError(ex.message || 'Failed to log substitution'); setSubmitting(false) }
  }

  if (!open || !item) return null

  const fields = editorFieldsForType(item.schedule_type)
  function curVal(fld) {
    return fld.target.kind === 'item_label' ? (item.item_label || '') : ((item.attrs && item.attrs[fld.target.key]) || '')
  }
  function propVal(fld) {
    return fld.target.kind === 'item_label' ? itemLabel : (attrs[fld.target.key] || '')
  }
  function setProp(fld, v) {
    if (fld.target.kind === 'item_label') setItemLabel(v)
    else setAttr(fld.target.key, v)
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="ScheduleSubstitutionModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 560, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>Log Substitution Request</span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginBottom: 16 }}>
          {item.schedule_type}{item.room_string ? ' · ' + item.room_string : ''} — this change goes to internal review before it applies to the item.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Column headers */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Current (issued)</div>
            <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#1a6b5e', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Proposed</div>
          </div>

          {fields.map((fld, i) => (
            <div key={i}>
              <label style={labelStyle}>{fld.label}</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <input style={roStyle} value={curVal(fld)} disabled readOnly />
                <input style={inputStyle} value={propVal(fld)} onChange={e => setProp(fld, e.target.value)} />
              </div>
            </div>
          ))}

          <div>
            <label style={labelStyle}>Spec section</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <input style={roStyle} value={item.spec_section || ''} disabled readOnly />
              <input style={inputStyle} value={specSection} onChange={e => setSpecSection(e.target.value)} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Reason for substitution <span style={{ color: '#c0392b' }}>*</span></label>
            <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical', fontFamily: '"DM Sans",sans-serif' }}
              placeholder="e.g. Specified model discontinued; proposed equivalent from same manufacturer"
              value={reason} onChange={e => setReason(e.target.value)} />
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSave} disabled={submitting} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: submitting ? 'var(--aj-bg-muted)' : '#07272D', color: submitting ? 'var(--aj-text-muted)' : '#fff', border: 'none', borderRadius: 4, cursor: submitting ? 'default' : 'pointer' }}>
              {submitting ? 'Submitting…' : 'Log substitution request'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
